import * as _ from 'lodash';
import { FormEvent, useState } from 'react';
import { INewFileUpload, IDocumentsVersion, IVersionedFile } from '@nkr/models/index';
import { Button, FormGroup, Col, FormControl, ControlLabel, Well, Row, Label } from 'react-bootstrap';
import UploadFileDetails from './upload-file-details';
import { VersionedFileEntityType } from '@nkr/enums/versioned-file-entity-type';

export enum UploadFileType {
    WithVersionControl = 0,
    Standard = 1
}

export enum NewFileDisplayType {
    AdminData = 1,
    SrtrData = 2,
    CenterData = 3
}

interface IProps {
    fileEntityType: VersionedFileEntityType,
    labelText: string,
    modalTitle?: string,
    valid: boolean,
    disable: boolean,
    type: UploadFileType,
    newFileDisplayType?: NewFileDisplayType,
    marginBottom?: number,
    newFile: INewFileUpload,
    dimensions?: { label: number, labelOffset?: number, existing: number, select: number, new: { size: number, offset: number } },
    existingFile?: string,
    documentsVersions?: IDocumentsVersion[],
    versionedFiles?: IVersionedFile[],
    withVersionHistory?: boolean,
    downloadById?: boolean,
    validationState?: 'success' | 'warning' | 'error',
    onSelectFile: (fileEntityType: VersionedFileEntityType, file: File, documentsVersionId: number, date: string) => void,
    onRemoveFile: (fileEntityType: VersionedFileEntityType) => void,
    onDownloadFile?: (id: number) => void
}

const UploadFile = (props: IProps) => {
    const [showNewFileModal, setShowNewFileModal] = useState(false);

    const handleView = () => {
        if (props.downloadById == null) {
            window.open(props.existingFile, '_blank');
        } else {
            props.onDownloadFile(0);
        }
    };

    const handleRemoveNewFile = () => {
        props.onRemoveFile(props.fileEntityType);
    };

    const handleShowNewFile = () => {
        setShowNewFileModal(true);
    };

    const handleCloseNewFile = () => {
        setShowNewFileModal(false);
    };

    const handleNewFileSubmit = (newFile: File, newFileVersionId: number, newFileDate: string) => {
        setShowNewFileModal(false);
        props.onSelectFile(props.fileEntityType, newFile, newFileVersionId, newFileDate);
    };

    const handleFileDownload = (e: FormEvent<HTMLAnchorElement>) => {
        e.stopPropagation();
        e.preventDefault();

        let id = parseInt(e.currentTarget.id);

        props.onDownloadFile(id);
    };

    const handleVersionedFileDownload = (id: number) => {
        props.onDownloadFile(id);
    };

    const getDimensions = () => {
        let dimensions = props.dimensions == null ? { label: 4, existing: 5, select: 3, new: { size: 12, offset: 0 } } : props.dimensions;
        return dimensions;
    }

    const renderAdminWithVersionControl = () => {
        return (
            <>
                <div style={{ marginBottom: 5 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        {props.labelText != '' && (
                            <div style={{ fontWeight: 'bold' }}>{props.labelText}</div>
                        )}

                        <Button bsStyle='primary' bsSize='xs' onClick={handleShowNewFile}>Select New File</Button>
                    </div>

                    {props.versionedFiles.map((item, index) => {
                        return (
                            <>
                                <a id={item.id.toString()} key={'vf-' + index} style={{ fontSize: 12, cursor: 'default' }} target='_blank' onClick={handleFileDownload}>View {item.documentsVersion.versionNumber}</a>{' '}
                            </>
                        )
                    })}

                    {props.versionedFiles.length == 0 && (
                        <i>No existing documents of this type...</i>
                    )}

                    {props.newFile != null && (
                        <div style={{ marginTop: 5 }}>{renderNewDocumentInfo()}</div>
                    )}
                </div>

                <UploadFileDetails
                    show={showNewFileModal}
                    title={props.modalTitle}
                    documentVersions={props.documentsVersions}
                    type={props.type}
                    withVersionHistory={true}
                    onSubmit={handleNewFileSubmit}
                    onCancel={handleCloseNewFile}
                />
            </>
        );
    };

    const renderCenterWithVersionControl = () => {
        return (
            <>
                <FormGroup style={{ marginBottom, verticalAlign: 'bottom' }} validationState={props.validationState}>
                    <Col xs={12} lg={dimensions.label} lgOffset={dimensions.labelOffset} componentClass={ControlLabel}>{props.labelText}:</Col>

                    <Col xs={12} lg={dimensions.existing}>
                        {props.versionedFiles.map((item, index) => {
                            return (
                                <Button bsSize='sm' key={'vf-' + index} style={{ fontSize: 14 }} onClick={() => handleVersionedFileDownload(item.id)}>View</Button>
                            );
                        })}
                        {props.versionedFiles.length === 0 && (
                            <i>No existing documents of this type...</i>
                        )}
                    </Col>
                    <Col xs={12} lg={dimensions.select}>
                        <Button bsStyle='primary' bsSize='sm' onClick={handleShowNewFile}>Select New File</Button>
                    </Col>

                    <Col xs={12} lg={dimensions.new.size} lgOffset={dimensions.new.offset}>
                        {props.newFile != null && (
                            <div style={{ marginTop: 5 }}>{renderNewDocumentInfo()}</div>
                        )}
                    </Col>
                </FormGroup >

                <UploadFileDetails
                    show={showNewFileModal}
                    title={props.modalTitle}
                    documentVersions={props.documentsVersions}
                    type={props.type}
                    withVersionHistory={false}
                    onSubmit={handleNewFileSubmit}
                    onCancel={handleCloseNewFile}
                />
            </>
        );
    };

    const renderNewDocumentInfo = () => {
        let { file, documentsVersionId, date } = props.newFile;
        let documentVersion = (props.documentsVersions ?? []).find(x => x.id == documentsVersionId);

        if (props.newFileDisplayType == NewFileDisplayType.SrtrData) {
            return (
                <Well style={{ padding: '7.5px 15px', marginBottom: 0 }}>
                    <div>
                        <span style={{ paddingRight: 10 }}><strong>File to Upload:</strong></span>
                        <span style={{ paddingRight: 20 }}>{file.name}</span>
                    </div>

                    {props.type == UploadFileType.WithVersionControl && (
                        <>
                            <div>
                                <span style={{ paddingRight: 10 }}><strong>Version:</strong></span>
                                <span style={{ paddingRight: 20 }}>{documentVersion.versionNumber}</span>
                            </div>
                            <div>
                                <span style={{ paddingRight: 10 }}><strong>Effective Date:</strong></span>
                                <span style={{ paddingRight: 20 }}>{date}</span>
                            </div>
                        </>
                    )}

                    <Button bsStyle='danger' bsSize='xs' onClick={handleRemoveNewFile}>Remove</Button>
                </Well>
            );
        }

        if (props.newFileDisplayType == NewFileDisplayType.CenterData) {
            return (
                <Well style={{ padding: '7.5px 15px', marginBottom: 0 }}>
                    <Row>
                        <Col xs={12}><strong>File to Upload</strong></Col>
                    </Row>
                    <Row>
                        <Col xs={12}><div>{file.name}</div></Col>
                    </Row>

                    {props.type == UploadFileType.WithVersionControl && (
                        <>
                            <Row>
                                <Col xs={12}><strong>Version</strong></Col>
                            </Row>
                            <Row>
                                <Col xs={12}>{documentVersion.versionNumber}</Col>
                            </Row>
                            <Row>
                                <Col xs={12}><strong>Effective Date</strong></Col>
                            </Row>
                            <Row>
                                <Col xs={12}>{date}</Col>
                            </Row>
                        </>
                    )}

                    <Row>
                        <Col xs={12}>
                            <Button bsStyle='danger' bsSize='sm' onClick={handleRemoveNewFile}>Remove</Button>
                        </Col>
                    </Row>
                </Well>
            );
        }

        if (props.newFileDisplayType == NewFileDisplayType.AdminData) {
            return (
                <Well style={{ padding: '7.5px 15px', marginBottom: 0, fontSize: 12 }}>
                    <Row>
                        <Col xs={10}>
                            <Row>
                                <Col xs={4}><strong>File to Upload:</strong></Col>
                                <Col xs={8}><div>{file.name}</div></Col>
                            </Row>

                            {props.type == UploadFileType.WithVersionControl && (
                                <>
                                    <Row>
                                        <Col xs={4}><strong>Version:</strong></Col>
                                        <Col xs={8}>{documentVersion.versionNumber}</Col>
                                    </Row>
                                    <Row>
                                        <Col xs={4}><strong>Effective Date:</strong></Col>
                                        <Col xs={8}>{date}</Col>
                                    </Row>
                                </>
                            )}

                        </Col>
                        <Col xs={2}>
                            <Button bsStyle='danger' bsSize='sm' onClick={handleRemoveNewFile}>Remove</Button>
                        </Col>
                    </Row>
                </Well>
            );
        }
    };

    if (props.disable)
        return null;

    const marginBottom = props.marginBottom != null ? props.marginBottom : 20;
    const dimensions = getDimensions();

    if (props.type == UploadFileType.WithVersionControl && props.withVersionHistory) {
        return renderAdminWithVersionControl();
    }

    if (props.type == UploadFileType.WithVersionControl && !props.withVersionHistory) {
        return renderCenterWithVersionControl();
    }

    return (
        <>
            <FormGroup style={{ marginBottom }} validationState={props.validationState}>
                <Col xs={12} lg={dimensions.label} lgOffset={dimensions.labelOffset} componentClass={ControlLabel}>{props.labelText}:</Col>

                <Col xs={12} lg={dimensions.existing}>
                    {props.type == UploadFileType.Standard && props.existingFile && props.downloadById == null && (
                        <Button bsSize='sm' disabled={props.existingFile == null || props.existingFile == ''} onClick={handleView}>View</Button>
                    )}

                    {props.type == UploadFileType.Standard && props.existingFile && props.downloadById == true && (
                        <Button bsSize='sm' disabled={props.existingFile == null || props.existingFile == ''} onClick={handleView}>View</Button>
                    )}

                    {props.existingFile == '' && (
                        <div className='form-control-static'>
                            <Label>No current file</Label>
                        </div>
                    )}
                </Col>

                <Col xs={12} lg={dimensions.select}>
                    <Button bsStyle='primary' bsSize='sm' onClick={handleShowNewFile}>Select New File</Button>
                </Col>

                <Col xs={12} lg={dimensions.new.size} lgOffset={dimensions.new.offset}>
                    {props.newFile != null && (
                        <div style={{ marginTop: 5 }}>{renderNewDocumentInfo()}</div>
                    )}
                </Col>
            </FormGroup>

            <UploadFileDetails
                show={showNewFileModal}
                title={props.modalTitle}
                documentVersions={props.documentsVersions}
                type={props.type}
                withVersionHistory={false}
                onSubmit={handleNewFileSubmit}
                onCancel={handleCloseNewFile}
            />
        </>
    );
}

export default UploadFile;